import { useState } from "react";
import { FaSearch, FaTrashAlt, FaMagic, FaShieldAlt } from "react-icons/fa";
import {
  ListSkeleton,
  EmptyState,
  SearchInput,
  ListView,
} from "../../shared";
import PasswordConfirmModal from "../../../Components/Utils/PasswordConfirmModal";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useLazyGetDeviceMqttUserQuery,
  useLazyGetUserAclQuery,
  useResetUserAclMutation,
  useApplyDeviceAclMutation,
} from "../../../store";

const ACTION_BADGE = {
  publish: "bg-emerald-50 text-emerald-700 border-emerald-100",
  subscribe: "bg-blue-50 text-blue-700 border-blue-100",
  all: "bg-gray-100 text-gray-700 border-gray-200",
};
const PERMISSION_BADGE = {
  allow: "bg-emerald-50 text-emerald-700 border-emerald-100",
  deny: "bg-red-50 text-red-700 border-red-100",
};

/**
 * EMQX ACL Yonetim — cihaz mqttUser'inin authorization kurallarini
 * goruntule, sifirla (template'e dus) veya cihaz-odakli standart izinleri uygula.
 * Standart preset URQS publish iznini acar → depo/kota cevabi calisir.
 */
export default function AclTab() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const twoFactorEnabled = auth?.twoFactorEnabled === true;
  const { showSuccess, showError } = useAlert();

  const [serialNo, setSerialNo] = useState("");
  const [resolved, setResolved] = useState(null); // { serialNo, username }
  const [confirm, setConfirm] = useState(null); // "reset" | "apply" | null

  const [findUser, { isFetching: isResolving }] = useLazyGetDeviceMqttUserQuery();
  const [fetchAcl, { data: aclData, isFetching: isAclLoading }] =
    useLazyGetUserAclQuery();
  const [resetAcl, { isLoading: isResetting }] = useResetUserAclMutation();
  const [applyAcl, { isLoading: isApplying }] = useApplyDeviceAclMutation();

  const rules = aclData?.data?.rules || [];

  const handleSearch = async () => {
    const sn = serialNo.trim();
    if (!sn) return;
    try {
      const res = await findUser({ token, serialNo: sn }).unwrap();
      const username = res?.data?.username;
      setResolved({ serialNo: sn, username });
      await fetchAcl({ token, username }).unwrap();
    } catch (err) {
      setResolved(null);
      showError(err?.data?.message || "Cihaz / mqttUser bulunamadı");
    }
  };

  const doReset = async (creds) => {
    try {
      await resetAcl({ token, username: resolved.username, ...creds }).unwrap();
      await fetchAcl({ token, username: resolved.username });
      showSuccess("ACL sıfırlandı (global template'e düştü).");
    } catch (err) {
      showError(err?.data?.message || "Reset başarısız oldu.");
    } finally {
      setConfirm(null);
    }
  };

  const doApply = async (creds) => {
    try {
      await applyAcl({
        token,
        username: resolved.username,
        serialNo: resolved.serialNo,
        ...creds,
      }).unwrap();
      await fetchAcl({ token, username: resolved.username });
      showSuccess("Standart cihaz izinleri uygulandı.");
    } catch (err) {
      showError(err?.data?.message || "İzinler uygulanamadı.");
    } finally {
      setConfirm(null);
    }
  };

  const busy = isResolving || isAclLoading;

  return (
    <div className="h-full flex flex-col">
      {/* Arama */}
      <div className="px-3 py-2.5 sm:p-4 md:p-6 shrink-0 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1">
          <SearchInput
            value={serialNo}
            onChange={(e) => setSerialNo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="serialNo gir (ör. BYN262000058)"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={!serialNo.trim() || isResolving}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          <FaSearch className="w-3.5 h-3.5" />
          Ara
        </button>
      </div>

      {/* Cihaz kartı + aksiyonlar */}
      {resolved?.username && (
        <div className="px-3 sm:px-4 md:px-6 py-3 shrink-0 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <FaShieldAlt className="w-4 h-4 text-indigo-500" />
              {resolved.serialNo}
            </div>
            <div className="text-xs text-gray-500 font-mono mt-0.5">
              mqttUser: {resolved.username}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirm("reset")}
              disabled={isResetting || isApplying}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-100 rounded-md transition-colors disabled:opacity-60"
            >
              <FaTrashAlt className="w-3 h-3" />
              Reset
            </button>
            <button
              type="button"
              onClick={() => setConfirm("apply")}
              disabled={isResetting || isApplying}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 rounded-md transition-colors disabled:opacity-60"
            >
              <FaMagic className="w-3 h-3" />
              Standart izinleri uygula
            </button>
          </div>
        </div>
      )}

      {/* İçerik */}
      <div className="flex-1 min-h-0 flex flex-col relative">
        {/* Overlay'i ListSkeleton kendisi render eder — burada ikinci bir
            MascotLoadingOverlay olursa metinler üst üste biner. */}
        {!resolved?.username ? (
          <div className="p-3 sm:p-4 md:p-6">
            {busy ? (
              <ListSkeleton message="ACL yükleniyor..." />
            ) : (
              <EmptyState
                title="Cihaz ara"
                description="Bir cihazın EMQX ACL kurallarını görmek için yukarıya serialNo girip Ara'ya bas."
              />
            )}
          </div>
        ) : (
          <ListView
            columns={[
              {
                key: "topic",
                label: "Topic",
                cellClassName: "font-mono text-xs text-gray-900 break-all whitespace-normal",
              },
              {
                key: "action",
                label: "Action",
                render: (r) => (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${
                      ACTION_BADGE[r.action] || ACTION_BADGE.all
                    }`}
                  >
                    {r.action}
                  </span>
                ),
              },
              {
                key: "permission",
                label: "Permission",
                render: (r) => (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${
                      PERMISSION_BADGE[r.permission] || PERMISSION_BADGE.deny
                    }`}
                  >
                    {r.permission}
                  </span>
                ),
              },
            ]}
            data={rules}
            rowKey={(r) => `${r.topic}-${r.action}-${r.permission}`}
            renderMobileCard={(r) => (
              <div className="bg-white px-3.5 py-3 border-b border-gray-200">
                <div className="font-mono text-xs text-gray-900 break-all mb-1.5">
                  {r.topic}
                </div>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${
                      ACTION_BADGE[r.action] || ACTION_BADGE.all
                    }`}
                  >
                    {r.action}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${
                      PERMISSION_BADGE[r.permission] || PERMISSION_BADGE.deny
                    }`}
                  >
                    {r.permission}
                  </span>
                </div>
              </div>
            )}
            isLoading={isAclLoading}
            loadingNode={
              <div className="p-3 sm:p-4 md:p-6">
                <ListSkeleton message="ACL yükleniyor..." />
              </div>
            }
            empty={
              <div className="p-4 md:p-6">
                <EmptyState
                  title="Özel ACL kuralı yok"
                  description="Bu mqttUser'ın per-device ACL kuralı yok — global template geçerli. 'Standart izinleri uygula' ile cihaz-odaklı kuralları yazabilirsin."
                />
              </div>
            }
          />
        )}
      </div>

      {/* Onay + şifre/2FA modalı (prod'da controlPassword+2FA zorunlu) */}
      <PasswordConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={confirm === "reset" ? doReset : doApply}
        isLoading={isResetting || isApplying}
        twoFactorEnabled={twoFactorEnabled}
        title={confirm === "reset" ? "ACL Reset" : "Standart İzinleri Uygula"}
        subtitle={
          confirm === "reset"
            ? `${resolved?.username} per-device ACL'i silinecek (template'e düşer). Onaylamak için şifrenizi girin.`
            : `${resolved?.serialNo} için cihaz-odaklı standart ACL uygulanacak (URQS dahil). Onaylamak için şifrenizi girin.`
        }
        confirmButtonText="Onayla"
      />
    </div>
  );
}
